import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { useLocationTypesContext } from "@/pages/configurations/locations/context/LocationType";
import { INITIAL_VALUES } from "@/pages/configurations/locations/models/Location/FormInitialValues";
import { Location } from "@/pages/configurations/locations/models/Location/Location";
import { createLocation } from "@/pages/configurations/locations/repositories/Location";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateLocation = ({ show, onHide }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [error, setError] = useState<string | null>(null);

	const classes = useStyles();

	const {
		state: { rootTypes },
	} = useRootTypeContext();
	const {
		state: { locationTypes },
	} = useLocationTypesContext();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Location>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<Location> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await createLocation(data);

			if (res.ok) {
				setMessage("The location was created successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="createLocation"
					title="Create a new location"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "300px" }}
								>
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={4} xs={12}>
											<Controller
												name="locationId"
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Location ID"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.locationId}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.locationId?.type === "positive" && (
												<span className={classes.errorLabel}>
													Location ID must be different from 0
												</span>
											)}
											{errors.locationId?.type === "required" && (
												<span className={classes.errorLabel}>
													Location ID is required
												</span>
											)}
										</Grid>
										<Grid item md={8} xs={12}>
											<Controller
												name="locationName"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.locationName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.locationName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>

										<Grid item md={6} xs={12}>
											<Typography>Status</Typography>
											<Controller
												name="isActive"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Active" : "Inactive"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>

										<Grid item md={4} xs={12}>
											<Controller
												name="sortOrder"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														type={"number"}
														label="Sort Order"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.sortOrder}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.sortOrder?.type === "required" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="rootTypeId"
												control={control}
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															Root type
														</InputLabel>
														<Select
															error={!!errors.rootTypeId}
															value={field.value}
															onChange={field.onChange}
														>
															{rootTypes.map((type) => {
																return (
																	<MenuItem
																		value={type.rootTypeId}
																		key={type.rootTypeId}
																	>
																		{type.rootTypeName}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
												)}
											/>
											{errors.rootTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="locationTypeId"
												control={control}
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															Location type
														</InputLabel>
														<Select
															error={!!errors.locationTypeId}
															value={field.value}
															onChange={field.onChange}
														>
															{locationTypes.map((type) => {
																return (
																	<MenuItem
																		value={type.locationTypeId}
																		key={type.locationTypeId}
																	>
																		{type.locationTypeName}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
												)}
											/>
											{errors.locationTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>

				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
